import styled from 'styled-components';
import Image from 'next/image';
import Link from 'next/link';
import { analytics } from '../../lib/analytics';
import { shimmerDataURL, SHOP_TILE_SIZES } from '../../lib/imageShimmer';

interface IShopItemCard {
  name: string;
  imageUrl: string;
  category: any;
  tags: string[];
  slug: string;
  inventory: any[];
  categorySlug: string;
}

const ShopItemCardWrapper = styled.a`
  position: relative;
  display: block;
  border-radius: 12px;
  border: 1px solid #e5e8f0;
  background: #ffffff;
  box-shadow: 0px 12px 30px rgb(0 27 31 / 6%);
  overflow: hidden;
  transition: transform 0.25s ease, box-shadow 0.25s ease;
  &:hover {
    transform: translateY(-3px);
    box-shadow: 0px 22px 48px rgb(0 27 31 / 12%);
  }
  &:hover img {
    transform: scale(1.05);
  }
`;

const ShopItemImage = styled.div`
  position: relative;
  height: 220px;
  padding: 18px;
  /* Flat, near-white panel so multiply blends uniformly across the tile. */
  background: #fbfbfc;
  border-bottom: 1px solid #eef1f6;
  overflow: hidden;
  @media only screen and (min-width: 768px) {
    height: 260px;
  }
  & img {
    transition: transform 0.4s ease;
    /* Auto-blends white / off-white image backgrounds into the panel so
       opaque JPGs and transparent PNGs read consistently. */
    mix-blend-mode: multiply;
  }
`;

const ShopItemImageInner = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
`;

const ShopItemDetails = styled.div`
  padding: 12px 16px;
  & > p {
  }
`;

const ShopItemTagRow = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 5px;
`;

export const ShopItemTag = styled.div`
  border-radius: 4px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  background-color: #e5e8f0;
  padding: 0 6px;
`;

export const ShopItemCategory = styled(ShopItemTag)`
  position: absolute;
  top: 8px;
  right: 8px;
  z-index: 2;
`;

const ShopItemCard = ({
  imageUrl,
  name,
  tags,
  category,
  slug,
  inventory,
  categorySlug,
}: IShopItemCard) => {
  return (
    <Link href={`/shop/category/${categorySlug}/item/${slug}`} legacyBehavior>
      <ShopItemCardWrapper
        onClick={() => {
          analytics.track('User Clicked Shop Item', {
            category: categorySlug,
            slug,
            name,
          });
        }}
      >
        <ShopItemCategory>{category?.name}</ShopItemCategory>
        <ShopItemImage>
          <ShopItemImageInner>
            <Image
              src={imageUrl}
              alt={name}
              layout="fill"
              objectFit="contain"
              sizes={SHOP_TILE_SIZES}
              quality={90}
              placeholder="blur"
              blurDataURL={shimmerDataURL()}
            />
          </ShopItemImageInner>
        </ShopItemImage>
        <ShopItemDetails>
          <p className="text-md font-bold">{name}</p>
          <p className="text-xs font-medium">{inventory?.length} Available</p>
          {tags.length > 0 && (
            <ShopItemTagRow>
              {tags.map((tag, index) => (
                <ShopItemTag key={index}>{tag}</ShopItemTag>
              ))}
            </ShopItemTagRow>
          )}
        </ShopItemDetails>
      </ShopItemCardWrapper>
    </Link>
  );
};

export default ShopItemCard;
